import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';
import { Button, Card } from '../components/ui';

/**
 * WBO verification panel.
 *
 * The WBO verify endpoint (`POST /wbo/topics/:id/verify`) requires at least two
 * independent HTTPS evidence URLs from different hosts before a story becomes
 * VERIFIED (a single source, or social/aggregator hosts, are not independent
 * proof). This panel is the missing UI for that flow: the owner adds the extra
 * independent evidence for a candidate and verifies it. It performs no research
 * itself and never invents a source.
 */

type Candidate = Awaited<ReturnType<typeof api.wbo.candidates>>['candidates'][number];

const IMAGE_STATUSES = ['NEEDS_REVIEW', 'ARCHIVAL_LICENSED', 'AI_ILLUSTRATION', 'UNAVAILABLE'] as const;

export function WboVerifyPanel({ onError, onMessage }: { onError: (error: unknown) => void; onMessage: (text: string) => void }) {
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [extraUrls, setExtraUrls] = useState<Record<string, string>>({});
  const [evergreenFlag, setEvergreenFlag] = useState<Record<string, boolean>>({});
  const [imageStatus, setImageStatus] = useState<Record<string, string>>({});
  const [onlyPending, setOnlyPending] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const result = await api.wbo.candidates(200); setCandidates(result.candidates); }
    catch (cause) { onError(cause); }
  }, [onError]);
  useEffect(() => { void load(); }, [load]);

  const rows = useMemo(() => candidates
    .filter((candidate) => candidate.verificationStatus !== 'REJECTED')
    .filter((candidate) => !onlyPending || candidate.verificationStatus !== 'VERIFIED')
    .sort((a, b) => (a.verificationStatus === b.verificationStatus ? 0 : a.verificationStatus === 'NEEDS_REVIEW' ? -1 : 1)), [candidates, onlyPending]);

  const verify = (candidate: Candidate) => {
    const typed = (extraUrls[candidate.id] ?? '').split(/[\s,]+/).map((value) => value.trim()).filter(Boolean);
    const sourceUrls = [...new Set([...candidate.sources.map((source) => source.url), ...typed])].filter((url) => /^https:\/\//i.test(url));
    return (async () => {
      setBusy(true);
      try {
        await api.wbo.verifyCandidate(candidate.id, { sourceUrls, evergreen: evergreenFlag[candidate.id] ?? true, imageStatus: imageStatus[candidate.id] ?? 'NEEDS_REVIEW' });
        await load();
        onMessage(`Zweryfikowano: ${candidate.title}. Historia jest teraz VERIFIED i może wejść do Long Studio.`);
      } catch (cause) { onError(cause); }
      finally { setBusy(false); }
    })();
  };

  return <Card title="Weryfikacja historii (WBO → Long)">
    <p className="muted">Historia staje się <strong>VERIFIED</strong> dopiero po podaniu co najmniej dwóch niezależnych źródeł HTTPS z różnych domen. Pojedynczy link albo wpis z mediów społecznościowych/agregatora nie wystarcza. Long Studio buduje filmy wyłącznie z historii VERIFIED.</p>
    <label><input type="checkbox" checked={onlyPending} onChange={(event) => setOnlyPending(event.target.checked)} /> Pokaż tylko niezweryfikowane ({candidates.filter((c) => c.verificationStatus !== 'VERIFIED' && c.verificationStatus !== 'REJECTED').length})</label>
    {rows.length === 0 ? <p className="muted">Brak historii do weryfikacji.</p> : <div className="table-wrap"><table className="table"><thead><tr><th>Historia</th><th>Istniejące źródła</th><th>Dodatkowe niezależne źródła (HTTPS)</th><th>Evergreen</th><th>Obraz</th><th>Status</th><th>Akcja</th></tr></thead><tbody>
      {rows.map((candidate) => <tr key={candidate.id}>
        <td><strong>{candidate.title}</strong><br /><small className="muted">{candidate.summary.slice(0, 160)}</small></td>
        <td>{candidate.sources.map((source) => <div key={source.url}><a href={source.url} target="_blank" rel="noreferrer">{source.title}</a></div>)}<small className="muted">{candidate.sources.length} źródło/źródeł</small></td>
        <td><textarea className="input" rows={3} placeholder="https://…&#10;https://…" value={extraUrls[candidate.id] ?? ''} onChange={(event) => setExtraUrls((current) => ({ ...current, [candidate.id]: event.target.value }))} /></td>
        <td><input type="checkbox" checked={evergreenFlag[candidate.id] ?? true} onChange={(event) => setEvergreenFlag((current) => ({ ...current, [candidate.id]: event.target.checked }))} /></td>
        <td><select className="input" value={imageStatus[candidate.id] ?? 'NEEDS_REVIEW'} onChange={(event) => setImageStatus((current) => ({ ...current, [candidate.id]: event.target.value }))}>{IMAGE_STATUSES.map((status) => <option key={status} value={status}>{status}</option>)}</select></td>
        <td>{candidate.verificationStatus}</td>
        <td><Button variant="primary" disabled={busy} onClick={() => void verify(candidate)}>Zweryfikuj</Button></td>
      </tr>)}
    </tbody></table></div>}
  </Card>;
}
